/**
 * MessageInspectorModal
 *
 * Shows messages peeked (non-destructively) from a RabbitMQ queue — payload plus
 * properties, including which service published each message.
 */

import { Modal, Stack, Group, Text, Badge, Code, Card, Loader, Alert } from '../../../../components/ui';
import { IconUser, IconClock, IconAlertCircle, IconInbox } from '@tabler/icons-react';
import type { PeekedMessage } from '../types';

interface MessageInspectorModalProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  messages: PeekedMessage[];
  loading: boolean;
  error?: string | null;
}

const formatTime = (iso?: string | null): string => {
  if (!iso) return 'unknown time';
  return new Date(iso).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'medium' });
};

const prettyJson = (raw: string): string => {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

export function MessageInspectorModal({
  opened,
  onClose,
  title,
  subtitle,
  messages,
  loading,
  error,
}: MessageInspectorModalProps) {
  return (
    <Modal opened={opened} onClose={onClose} title={title} size="xl">
      <Stack gap="md">
        {subtitle && (
          <Text size="sm" c="dimmed">
            {subtitle}
          </Text>
        )}

        {loading && (
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        )}

        {!loading && error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not load messages">
            {error}
          </Alert>
        )}

        {!loading && !error && messages.length === 0 && (
          <Alert color="blue" icon={<IconInbox size={16} />} title="Nothing to show">
            No messages are currently parked in this queue. Messages consumed immediately
            never wait here — only messages waiting for a consumer (or dead-lettered ones)
            can be peeked.
          </Alert>
        )}

        {!loading &&
          !error &&
          messages.map((m, index) => (
            <Card key={m.messageId || index} padding="sm">
              <Stack gap="xs">
                <Group justify="space-between" align="flex-start">
                  <Group gap="xs">
                    <Badge color="blue" variant="light">
                      {m.messageType}
                    </Badge>
                    {m.redelivered && (
                      <Badge color="orange" variant="light">
                        redelivered
                      </Badge>
                    )}
                  </Group>
                  <Text size="xs" c="dimmed">
                    {m.payloadBytes.toLocaleString()} bytes
                  </Text>
                </Group>

                <Group gap="lg">
                  <Group gap={4}>
                    <IconUser size={14} />
                    <Text size="xs">
                      Published by{' '}
                      <Text span fw={600}>
                        {m.publishedBy}
                      </Text>
                    </Text>
                  </Group>
                  <Group gap={4}>
                    <IconClock size={14} />
                    <Text size="xs" c="dimmed">
                      {formatTime(m.publishedAt)}
                    </Text>
                  </Group>
                </Group>

                {(m.messageId || m.correlationId) && (
                  <Group gap="lg">
                    {m.messageId && (
                      <Text size="xs" c="dimmed">
                        Message ID: <Code>{m.messageId}</Code>
                      </Text>
                    )}
                    {m.correlationId && (
                      <Text size="xs" c="dimmed">
                        Correlation: <Code>{m.correlationId}</Code>
                      </Text>
                    )}
                  </Group>
                )}

                {m.deathReason && (
                  <Alert color="red" icon={<IconAlertCircle size={16} />} title="Failed processing">
                    Reason <Text span fw={600}>{m.deathReason}</Text>
                    {m.deathQueue ? ` — originally from ${m.deathQueue}` : ''}
                    {m.deathCount > 0 ? ` (${m.deathCount} attempt${m.deathCount === 1 ? '' : 's'})` : ''}
                  </Alert>
                )}

                <Code block>{prettyJson(m.payload)}</Code>
              </Stack>
            </Card>
          ))}
      </Stack>
    </Modal>
  );
}
